import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { PublisherFilter, PublisherFormData, PublishersState } from '../../../types/publisher.types';
import apiClient from '../../../api/apiClient';
import { API_ENDPOINTS } from 'api/endPoints';

// Initial state
const initialState: PublishersState = {
  items: [],
  filteredItems: [],
  isLoading: false,
  error: null,
  filters: {
    searchTerm: '',
  },
  pagination: {
    page: 0,
    rowsPerPage: 10,
    totalCount: 0,
  },
};

// Async thunks
export const fetchPublishers = createAsyncThunk('publishers/fetchPublishers', async () => {
  const response = await apiClient.get(API_ENDPOINTS.PUBLISHER.PUBLISHERS.LIST);
  return response.data;
});

export const fetchFilteredPublishers = createAsyncThunk(
  'publishers/fetchFilteredPublishers',
  async (filters: PublisherFilter) => {
    const response = await apiClient.get(API_ENDPOINTS.PUBLISHER.PUBLISHERS.FILTER, {
      params: filters,
    });
    return response.data;
  }
);

export const createPublisher = createAsyncThunk(
  'publishers/createPublisher',
  async (publisherData: PublisherFormData) => {
    const response = await apiClient.post(API_ENDPOINTS.PUBLISHER.PUBLISHERS.CREATE, publisherData);
    return response.data;
  }
);

export const updatePublisher = createAsyncThunk(
  'publishers/updatePublisher',
  async ({ id, data }: { id: string; data: PublisherFormData }) => {
    const response = await apiClient.put(API_ENDPOINTS.PUBLISHER.PUBLISHERS.UPDATE(id), data);
    return response.data;
  }
);

export const deletePublisher = createAsyncThunk(
  'publishers/deletePublisher',
  async (id: string) => {
    await apiClient.delete(API_ENDPOINTS.PUBLISHER.PUBLISHERS.DELETE(id));
    return id;
  }
);

// Slice
const publishersSlice = createSlice({
  name: 'publishers',
  initialState,
  reducers: {
    setFilter: (state, action: PayloadAction<{ key: keyof PublisherFilter; value: string }>) => {
      const { key, value } = action.payload;
      state.filters[key] = value;
      
      // Reset pagination
      state.pagination.page = 0;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.page = action.payload;
    },
    setRowsPerPage: (state, action: PayloadAction<number>) => {
      state.pagination.rowsPerPage = action.payload;
      state.pagination.page = 0;
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
      state.pagination.page = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all publishers
      .addCase(fetchPublishers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPublishers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.items = action.payload;
        // IMPORTANT: create a separate array reference to avoid aliasing with items
        state.filteredItems = [...action.payload];
        state.pagination.totalCount = action.payload.length;
      })
      .addCase(fetchPublishers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch publishers';
      })
      
      // Fetch filtered publishers
      .addCase(fetchFilteredPublishers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchFilteredPublishers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.filteredItems = action.payload;
        state.pagination.totalCount = action.payload.length;
      })
      .addCase(fetchFilteredPublishers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch filtered publishers';
      })
      
      // Create publisher
      .addCase(createPublisher.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createPublisher.fulfilled, (state, action) => {
        state.isLoading = false;
        const created = action.payload;
        // Upsert into items
        const idxItems = state.items.findIndex(p => p.id === created.id);
        if (idxItems >= 0) {
          state.items[idxItems] = created;
        } else {
          state.items = [...state.items, created];
        }
        // Upsert into filteredItems
        const idxFiltered = state.filteredItems.findIndex(p => p.id === created.id);
        if (idxFiltered >= 0) {
          state.filteredItems[idxFiltered] = created;
        } else {
          state.filteredItems = [...state.filteredItems, created];
        }
        state.pagination.totalCount = state.filteredItems.length;
      })
      .addCase(createPublisher.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to create publisher';
      })
      
      // Update publisher
      .addCase(updatePublisher.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updatePublisher.fulfilled, (state, action) => {
        state.isLoading = false;
        const updatedPublisher = action.payload;
        
        // Update in items array
        const itemIndex = state.items.findIndex(item => item.id === updatedPublisher.id);
        if (itemIndex !== -1) {
          state.items[itemIndex] = updatedPublisher;
        }
        
        // Update in filtered items array
        const filteredIndex = state.filteredItems.findIndex(item => item.id === updatedPublisher.id);
        if (filteredIndex !== -1) {
          state.filteredItems[filteredIndex] = updatedPublisher;
        }
      })
      .addCase(updatePublisher.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to update publisher';
      })
      
      // Delete publisher
      .addCase(deletePublisher.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deletePublisher.fulfilled, (state, action) => {
        state.isLoading = false;
        const deletedId = action.payload;
        
        // Remove from items array
        state.items = state.items.filter(item => item.id !== deletedId);
        
        // Remove from filtered items array
        state.filteredItems = state.filteredItems.filter(item => item.id !== deletedId);
        
        state.pagination.totalCount = state.filteredItems.length;
      })
      .addCase(deletePublisher.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to delete publisher';
      });
  },
});

export const { setFilter, setPage, setRowsPerPage, resetFilters } = publishersSlice.actions;

export default publishersSlice.reducer;